import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { listFiles, getLogs, downloadFile, deleteFile } from "../../services/logViewerService";
import {
  TOKENS,
  Icon,
  ICONS,
  EmptyState,
  Spinner,
  btnPrimary,
  btnSecondary,
} from "../../components/common/DashboardUI";

// ── Log-level colour mapping ──────────────────────────────────────────────────
const LEVEL_STYLES = {
  emergency: { bg: "#fce7f3", color: "#9d174d", dot: "#db2777" },
  alert:     { bg: "#fce7f3", color: "#9d174d", dot: "#db2777" },
  critical:  { bg: "#fee2e2", color: "#991b1b", dot: "#ef4444" },
  error:     { bg: "#fee2e2", color: "#b91c1c", dot: "#ef4444" },
  warning:   { bg: "#fef3c7", color: "#92400e", dot: "#f59e0b" },
  notice:    { bg: "#dbeafe", color: "#1e40af", dot: "#3b82f6" },
  info:      { bg: "#dbeafe", color: "#1e40af", dot: "#3b82f6" },
  debug:     { bg: "#f3f4f6", color: "#374151", dot: "#9ca3af" },
};
const levelStyle = (lvl) =>
  LEVEL_STYLES[String(lvl || "").toLowerCase()] ?? { bg: "#f3f4f6", color: "#374151", dot: "#9ca3af" };

const LEVELS = ["", "emergency", "alert", "critical", "error", "warning", "notice", "info", "debug"];

// ── Helpers ───────────────────────────────────────────────────────────────────
function LevelBadge({ level }) {
  const s = levelStyle(level);
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-0.5 rounded-full capitalize whitespace-nowrap"
      style={{ backgroundColor: s.bg, color: s.color }}
    >
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.dot }} />
      {level || "unknown"}
    </span>
  );
}

function errMessage(err, fallback = "An error occurred") {
  return err?.response?.data?.message || fallback;
}

// ── Delete confirmation modal ─────────────────────────────────────────────────
function DeleteModal({ file, onClose, onConfirm, busy }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col items-center text-center gap-4">
          <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ backgroundColor: "#fee2e2" }}>
            <Icon path={ICONS.x} className="w-7 h-7" stroke="#ef4444" width={2.2} />
          </div>
          <div>
            <h2 className="text-lg font-bold mb-1" style={{ color: TOKENS.NAVY }}>Delete Log File?</h2>
            <p className="text-sm text-gray-500">
              This will permanently delete <span className="font-semibold text-gray-700">{file?.name ?? file?.identifier}</span> and cannot be undone.
            </p>
          </div>
          <div className="flex gap-3 w-full mt-2">
            <button className={`${btnSecondary} flex-1`} onClick={onClose} disabled={busy}>Cancel</button>
            <button
              className="flex-1 inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 active:bg-red-800 disabled:bg-red-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg text-sm px-4 py-2.5 transition shadow-sm"
              onClick={onConfirm}
              disabled={busy}
            >
              {busy ? <Spinner className="w-4 h-4 text-white" /> : <Icon path={ICONS.x} className="w-4 h-4" />}
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Log entry detail modal ────────────────────────────────────────────────────
function EntryModal({ entry, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden"
        style={{ maxHeight: "85vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100" style={{ background: `linear-gradient(135deg, ${TOKENS.SIDEBAR_TOP} 0%, ${TOKENS.SIDEBAR_BOTTOM} 100%)` }}>
          <div className="flex items-center gap-3">
            <LevelBadge level={entry?.level_name ?? entry?.level} />
            <span className="text-white/70 text-xs">{entry?.datetime || "—"}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition"
          >
            <Icon path={ICONS.x} className="w-4 h-4" width={2} />
          </button>
        </div>
        {/* Body */}
        <div className="overflow-y-auto" style={{ maxHeight: "calc(85vh - 64px)" }}>
          <div className="p-6 space-y-4">
            {/* Message */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">Message</p>
              <p className="text-sm text-gray-800 font-medium leading-relaxed">{entry?.message || "—"}</p>
            </div>
            {/* Context / Extra */}
            {(entry?.context || entry?.extra) && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">Context</p>
                <pre
                  className="text-xs bg-gray-50 rounded-xl p-4 overflow-x-auto border border-gray-100"
                  style={{ color: TOKENS.NAVY, fontFamily: "'Fira Mono', 'Cascadia Code', monospace", lineHeight: 1.7 }}
                >
                  {JSON.stringify(entry.context ?? entry.extra, null, 2)}
                </pre>
              </div>
            )}
            {/* Full text / stack trace — the raw log line including stack frames */}
            {entry?.full_text && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">Full Log</p>
                <pre
                  className="text-xs bg-gray-900 text-green-300 rounded-xl p-4 overflow-x-auto"
                  style={{ fontFamily: "'Fira Mono', 'Cascadia Code', monospace", lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                >
                  {entry.full_text}
                </pre>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function LogViewer() {
  // Files sidebar
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);

  // Log entries
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });

  // Filters
  const [search, setSearch] = useState("");
  const [draftSearch, setDraftSearch] = useState("");
  const searchTimer = useRef(null);
  const [level, setLevel] = useState("");
  const [page, setPage] = useState(1);

  // UI state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [detailEntry, setDetailEntry] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // ── Load files on mount ────────────────────────────────────────────────────
  const loadFiles = useCallback(() => {
    setFilesLoading(true);
    listFiles()
      .then((data) => {
        setFiles(data);
        if (data.length > 0 && !selectedFile) setSelectedFile(data[0]);
      })
      .catch((err) => toast.error(errMessage(err, "Failed to load log files")))
      .finally(() => setFilesLoading(false));
  }, [selectedFile]);

  useEffect(() => { loadFiles(); }, []); // eslint-disable-line

  // ── Load logs whenever file / filters change ───────────────────────────────
  const loadLogs = useCallback(() => {
    if (!selectedFile) return;
    setLogsLoading(true);
    // Build exclude_levels: if a level is chosen, exclude all OTHER levels
    const allLevels = ["emergency","alert","critical","error","warning","notice","info","debug"];
    const excludeLevels = level ? allLevels.filter((l) => l !== level) : [];
    getLogs({
      // opcodesio v3 LogsController reads $request->query('file') — NOT file_identifier
      file: selectedFile.identifier,
      query: search || undefined,
      direction: "desc",
      // exclude_levels[] tells the backend which levels to hide
      ...(excludeLevels.length > 0 ? { exclude_levels: excludeLevels } : {}),
      per_page: 50,
      page,
    })
      .then((data) => {
        // opcodesio v3 shape: { file, logs: LogResource[], levelCounts, pagination: { current_page, last_page, total, ... } }
        setLogs(Array.isArray(data?.logs) ? data.logs : []);
        const pag = data?.pagination ?? {};
        setPagination({
          current_page: pag.current_page ?? 1,
          last_page: pag.last_page ?? 1,
          total: pag.total ?? 0,
        });
      })
      .catch((err) => toast.error(errMessage(err, "Failed to load logs")))
      .finally(() => setLogsLoading(false));
  }, [selectedFile, search, level, page]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  // Debounce search input
  const handleSearchInput = (val) => {
    setDraftSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearch(val);
      setPage(1);
    }, 450);
  };

  // ── Download ───────────────────────────────────────────────────────────────
  const handleDownload = async (file) => {
    try {
      const res = await downloadFile(file.identifier ?? file.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name ?? `${file.identifier}.log`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(errMessage(err, "Download failed"));
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await deleteFile(deleteTarget.identifier ?? deleteTarget.id);
      toast.success("Log file deleted");
      const remaining = files.filter((f) => (f.identifier ?? f.id) !== (deleteTarget.identifier ?? deleteTarget.id));
      setFiles(remaining);
      if ((selectedFile?.identifier ?? selectedFile?.id) === (deleteTarget.identifier ?? deleteTarget.id)) {
        setSelectedFile(remaining[0] ?? null);
      }
    } catch (err) {
      toast.error(errMessage(err, "Failed to delete log file"));
    } finally {
      setDeleteBusy(false);
      setDeleteTarget(null);
    }
  };

  // ── Level summary counts ───────────────────────────────────────────────────
  const levelCounts = useMemo(() => {
    const counts = {};
    logs.forEach((e) => {
      const k = String(e?.level || "unknown").toLowerCase();
      counts[k] = (counts[k] || 0) + 1;
    });
    return counts;
  }, [logs]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex h-full min-h-0" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
      {/* ── Sidebar ─────────────────────────────────────────────────────────── */}
      <aside
        className="flex-shrink-0 flex flex-col border-r border-gray-200 bg-white transition-all duration-300"
        style={{ width: sidebarOpen ? 260 : 0, overflow: sidebarOpen ? "visible" : "hidden", minWidth: sidebarOpen ? 220 : 0 }}
      >
        <div className="px-4 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `linear-gradient(135deg, ${TOKENS.SIDEBAR_TOP}, ${TOKENS.PRIMARY})` }}>
              <Icon path={ICONS.attendance} className="w-4 h-4" stroke="white" width={2} />
            </div>
            <p className="font-bold text-sm" style={{ color: TOKENS.NAVY }}>Log Files</p>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {filesLoading ? (
            <div className="flex justify-center py-8"><Spinner /></div>
          ) : files.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-8">No log files found</p>
          ) : (
            <ul className="space-y-1">
              {files.map((file) => {
                const id = file.identifier ?? file.id;
                const sel = (selectedFile?.identifier ?? selectedFile?.id) === id;
                return (
                  <li key={id}>
                    <button
                      className="w-full text-left rounded-xl px-3 py-2.5 transition group"
                      style={{
                        background: sel ? `linear-gradient(135deg, ${TOKENS.SIDEBAR_TOP}18, ${TOKENS.PRIMARY}22)` : "transparent",
                        border: sel ? `1.5px solid ${TOKENS.PRIMARY}40` : "1.5px solid transparent",
                      }}
                      onClick={() => { setSelectedFile(file); setPage(1); }}
                    >
                      <p className="text-xs font-semibold truncate" style={{ color: sel ? TOKENS.PRIMARY : TOKENS.NAVY }}>
                        {file.name ?? id}
                      </p>
                      {file.size && (
                        <p className="text-xs text-gray-400 mt-0.5">{file.size}</p>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </aside>

      {/* ── Main content ────────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 bg-gray-50">
        {/* Top bar */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex flex-wrap items-center gap-3">
            {/* Sidebar toggle */}
            <button
              onClick={() => setSidebarOpen((v) => !v)}
              className="w-9 h-9 rounded-lg flex items-center justify-center border border-gray-200 text-gray-500 hover:bg-gray-50 transition flex-shrink-0"
              title={sidebarOpen ? "Hide sidebar" : "Show sidebar"}
            >
              <Icon path={ICONS.menu} className="w-5 h-5" />
            </button>

            {/* Page title */}
            <div className="flex-1 min-w-0">
              <h1 className="font-bold text-lg truncate" style={{ color: TOKENS.NAVY }}>
                Log Viewer
                {selectedFile && (
                  <span className="ml-2 text-sm font-normal text-gray-400">
                    — {selectedFile.name ?? selectedFile.identifier}
                  </span>
                )}
              </h1>
            </div>

            {/* Search */}
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <Icon path={ICONS.search} className="w-4 h-4 text-gray-400" />
              </span>
              <input
                id="log-search"
                type="text"
                placeholder="Search logs…"
                value={draftSearch}
                onChange={(e) => handleSearchInput(e.target.value)}
                className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition w-52"
              />
            </div>

            {/* Level filter */}
            <select
              id="log-level-filter"
              value={level}
              onChange={(e) => { setLevel(e.target.value); setPage(1); }}
              className="text-sm border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition bg-white"
            >
              {LEVELS.map((l) => (
                <option key={l} value={l}>{l === "" ? "All Levels" : l.charAt(0).toUpperCase() + l.slice(1)}</option>
              ))}
            </select>

            {/* Download */}
            {selectedFile && (
              <button
                id="log-download-btn"
                className={btnSecondary}
                onClick={() => handleDownload(selectedFile)}
                title="Download log file"
              >
                <Icon path={ICONS.download} className="w-4 h-4" />
                Download
              </button>
            )}

            {/* Delete */}
            {selectedFile && (
              <button
                id="log-delete-btn"
                className="inline-flex items-center justify-center gap-2 border border-red-200 text-red-600 hover:bg-red-50 font-semibold rounded-lg text-sm px-4 py-2.5 transition"
                onClick={() => setDeleteTarget(selectedFile)}
                title="Delete log file"
              >
                <Icon path={ICONS.x} className="w-4 h-4" />
                Delete
              </button>
            )}

            {/* Refresh */}
            <button
              id="log-refresh-btn"
              className={btnPrimary}
              onClick={loadLogs}
              disabled={logsLoading}
            >
              {logsLoading ? <Spinner className="w-4 h-4 text-white" /> : <Icon path={ICONS.clock} className="w-4 h-4" />}
              Refresh
            </button>
          </div>
        </div>

        {/* Level summary chips */}
        {Object.keys(levelCounts).length > 0 && (
          <div className="px-6 py-3 flex flex-wrap gap-2 bg-white border-b border-gray-100">
            {Object.entries(levelCounts).map(([lvl, cnt]) => {
              const s = levelStyle(lvl);
              return (
                <button
                  key={lvl}
                  onClick={() => { setLevel(level === lvl ? "" : lvl); setPage(1); }}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full capitalize transition border"
                  style={{
                    backgroundColor: level === lvl ? s.dot : s.bg,
                    color: level === lvl ? "white" : s.color,
                    borderColor: level === lvl ? s.dot : "transparent",
                  }}
                >
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: level === lvl ? "white" : s.dot }} />
                  {lvl} ({cnt})
                </button>
              );
            })}
          </div>
        )}

        {/* Log entries table */}
        <div className="flex-1 overflow-auto p-6">
          {!selectedFile ? (
            <EmptyState icon={ICONS.attendance} text="Select a log file from the sidebar to view entries" />
          ) : logsLoading ? (
            <div className="flex items-center justify-center py-24"><Spinner /></div>
          ) : logs.length === 0 ? (
            <EmptyState icon={ICONS.search} text="No log entries found for the current filters" />
          ) : (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: `linear-gradient(135deg, ${TOKENS.SIDEBAR_TOP}, ${TOKENS.SIDEBAR_BOTTOM})` }}>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-white/80 uppercase tracking-wider w-36">Level</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-white/80 uppercase tracking-wider w-44">Timestamp</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-white/80 uppercase tracking-wider">Message</th>
                    <th className="px-4 py-3 w-16" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {logs.map((entry, idx) => {
                    return (
                      <tr
                        key={entry?.id ?? entry?.index ?? idx}
                        className="hover:bg-blue-50/40 transition-colors cursor-pointer group"
                        onClick={() => setDetailEntry(entry)}
                      >
                        <td className="px-4 py-3">
                          <LevelBadge level={entry?.level} />
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap font-mono">
                          {entry?.datetime || entry?.date || "—"}
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-gray-700 font-medium truncate max-w-md group-hover:text-blue-700 transition-colors">
                            {entry?.message || "—"}
                          </p>
                          {(entry?.context || entry?.extra) && (
                            <p className="text-xs text-gray-400 mt-0.5 truncate max-w-sm">
                              {typeof (entry?.context || entry?.extra) === "string"
                                ? (entry?.context || entry?.extra)
                                : JSON.stringify(entry?.context || entry?.extra).slice(0, 120)}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className="text-xs text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity font-medium"
                          >
                            View
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.last_page > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-400">
                Page {pagination.current_page} of {pagination.last_page}
                {pagination.total > 0 && ` · ${pagination.total.toLocaleString()} entries`}
              </p>
              <div className="flex gap-2">
                <button
                  className={btnSecondary}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={pagination.current_page <= 1 || logsLoading}
                >
                  ← Prev
                </button>
                <button
                  className={btnSecondary}
                  onClick={() => setPage((p) => Math.min(pagination.last_page, p + 1))}
                  disabled={pagination.current_page >= pagination.last_page || logsLoading}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Modals ───────────────────────────────────────────────────────────── */}
      {deleteTarget && (
        <DeleteModal
          file={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          busy={deleteBusy}
        />
      )}
      {detailEntry && (
        <EntryModal entry={detailEntry} onClose={() => setDetailEntry(null)} />
      )}
    </div>
  );
}
